<script setup lang="ts">
import { computed } from 'vue'

/**
 * Belief elicitation by distribution: the participant spreads TOTAL_TOKENS over fixed
 * probability bins (0-9% ... 90-100%). More tokens in a bin = they think the real share of
 * players choosing action A is more likely to fall there. The model is the per-bin token
 * counts (length BIN_LABELS.length); it must sum to TOTAL_TOKENS before the vote can proceed
 * (the parent gates on `remaining === 0`).
 */
const BIN_LABELS = [
    '0-9%', '10-19%', '20-29%', '30-39%', '40-49%',
    '50-59%', '60-69%', '70-79%', '80-89%', '90-100%',
]
const TOTAL_TOKENS = 100

const model = defineModel<number[]>({ required: true })

const total = computed(() => model.value.reduce((sum, n) => sum + n, 0))
const remaining = computed(() => TOTAL_TOKENS - total.value)

// Tallest bar for scaling the histogram; never divide by zero, and keep a floor so a single
// allocated bin still reads as a visible bar rather than filling the whole area.
const peak = computed(() => Math.max(TOTAL_TOKENS / 5, ...model.value))

function barHeight(v: number): string {
    if (v <= 0) {
        return '0%'
    }
    return `${Math.max(4, (v / peak.value) * 100)}%`
}

// A bin can be raised at most by however many tokens are still unallocated, so the total can
// never exceed TOTAL_TOKENS. Lowering is always allowed.
function maxFor(i: number): number {
    return model.value[i] + remaining.value
}

function setBin(i: number, value: number | string): void {
    const next = [...model.value]
    next[i] = Number(value)
    model.value = next
}
</script>

<template>
    <!-- Capped a step below the reading column it sits in (max-w-240 in Vote.vue), so the
         histogram stays a figure inside the text rather than spanning it edge to edge. -->
    <div class="col items-stretch gap-4 w-full max-w-200">
        <div class="row items-end justify-between gap-1 sm:gap-2">
            <div v-for="(label, i) in BIN_LABELS" :key="label" class="col items-center gap-1.5 flex-1 min-w-0">
                <span class="bin-count font-semibold">{{ model[i] }}</span>
                <div class="bar-area">
                    <div class="bar" :style="{ height: barHeight(model[i]) }"></div>
                </div>
                <span class="bin-label leading-tight text-center text-blue-700">{{ label }}</span>
                <input
                    type="range"
                    class="v-slider"
                    min="0"
                    :max="maxFor(i)"
                    step="1"
                    :value="model[i]"
                    @input="setBin(i, ($event.target as HTMLInputElement).value)"
                    :aria-label="`Token per l'intervallo ${label}`"
                >
            </div>
        </div>
        <div class="row justify-between items-center px-1 text-sm">
            <span>Token assegnati:</span>
            <span class="font-semibold" :class="remaining === 0 ? 'text-blue-700' : 'text-pink-650'">
                {{ total }} / {{ TOTAL_TOKENS }}
            </span>
        </div>
    </div>
</template>

<style scoped>
@reference 'tailwindcss';
@reference '#app.css';

/* The histogram is the subject of its screen, so it grows with the viewport rather than sitting
   at phone size in the middle of a wide column. Bar area, bar width, slider and type all scale
   together on the same viewport range (roughly 780px to 1330px); every clamp rests on its lower
   bound below that, which is exactly the size the histogram had before, so phones are untouched. */
.bar-area {
    @apply w-full flex items-end justify-center;
    height: clamp(140px, 18vw, 240px);
}

/* The histogram bar grows from the bottom; height is set inline from the token count. */
.bar {
    @apply w-[70%] bg-blue rounded-t-[4px] transition-[height] duration-100;
    max-width: clamp(36px, 5vw, 64px);
}

/* The token count sits above its bar; `min-height` (not a fixed height) reserves the row when a
   bin is empty without clipping the number once the type scales up. */
.bin-count {
    font-size: clamp(var(--text-sm), 1.5vw, var(--text-md));
    min-height: 1.1em;
}

.bin-label {
    font-size: clamp(10px, 1.1vw, 14px);
    min-height: 24px;
}

/* Vertical native slider under each bar. writing-mode is the modern vertical form;
   the -webkit-appearance fallback covers older WebKit. */
.v-slider {
    writing-mode: vertical-lr;
    direction: rtl;
    -webkit-appearance: slider-vertical;
    appearance: slider-vertical;
    @apply w-6 cursor-pointer accent-blue;
    height: clamp(112px, 14vw, 190px);
}
</style>
